import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, formatPrice, formatApiError } from "@/lib/api";
import { Search, Check } from "lucide-react";

const STATUS_FLOW = ["Pending", "Confirmed", "Preparing", "Ready", "Out for Delivery", "Delivered"];

export default function TrackOrder() {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(searchParams.get("id") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (id) => {
    const q = (id ?? orderId).trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/track/${q}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Order not found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("id")) track(searchParams.get("id"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelled = order?.status === "Cancelled";
  const currentIdx = order ? STATUS_FLOW.indexOf(order.status) : -1;

  return (
    <div className="mx-auto max-w-2xl animate-fade-in px-4 py-12 sm:px-6 lg:px-8">
      <div className="text-center">
        <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Order Status</p>
        <h1 className="mt-2 font-heading text-4xl text-brand-dark">Track Your Order</h1>
      </div>

      <div className="mt-8 flex gap-2">
        <input
          data-testid="track-input"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && track()}
          placeholder="Enter Order ID e.g. TAA-ABC123"
          className="flex-1 rounded-full border border-brand-line bg-white px-5 py-3 text-brand-dark outline-none focus:border-transparent focus:ring-2 focus:ring-brand-gold"
        />
        <button
          data-testid="track-btn"
          onClick={() => track()}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-medium text-white hover:bg-brand-hover"
        >
          <Search className="h-4 w-4" /> Track
        </button>
      </div>

      {loading && <p className="mt-6 text-center text-brand-muted">Searching...</p>}
      {error && (
        <p data-testid="track-error" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-center text-red-600">
          {error}
        </p>
      )}

      {order && (
        <div data-testid="track-result" className="mt-8 rounded-2xl border border-brand-line bg-white p-6">
          <div className="flex items-center justify-between border-b border-brand-line pb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-muted">Order ID</p>
              <p className="font-heading text-2xl text-brand">{order.order_id}</p>
            </div>
            <span
              data-testid="track-status"
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                cancelled ? "bg-red-100 text-red-600" : "bg-brand-green/10 text-brand-green"
              }`}
            >
              {order.status}
            </span>
          </div>

          {!cancelled ? (
            <div className="mt-6 space-y-1">
              {STATUS_FLOW.map((s, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={s} className="flex items-center gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full ${
                          done ? "bg-brand-green text-white" : "border-2 border-brand-line bg-white text-brand-line"
                        }`}
                      >
                        {done ? <Check className="h-4 w-4" /> : <span className="h-2 w-2 rounded-full bg-brand-line" />}
                      </div>
                      {i < STATUS_FLOW.length - 1 && (
                        <div className={`h-6 w-0.5 ${i < currentIdx ? "bg-brand-green" : "bg-brand-line"}`} />
                      )}
                    </div>
                    <span className={`pb-6 text-sm font-medium ${done ? "text-brand-dark" : "text-brand-muted"}`}>{s}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-red-600">This order has been cancelled.</p>
          )}

          <div className="mt-4 border-t border-brand-line pt-4">
            <h3 className="font-heading text-lg text-brand-dark">Items</h3>
            <ul className="mt-2 space-y-1 text-sm text-brand-muted">
              {order.items.map((i, idx) => (
                <li key={idx}>
                  {i.name} ({i.variant_label}) ×{i.quantity} — {formatPrice(i.price * i.quantity)}
                  {i.customization && (
                    <span className="block text-xs italic text-brand">
                      {i.customization_label ? `${i.customization_label}: ` : ""}{i.customization}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-3 font-semibold text-brand-dark">Total: {formatPrice(order.total)}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-brand-muted">Payment:</span>
              <span
                data-testid="track-payment-status"
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  order.payment_status === "Payment Verified"
                    ? "bg-brand-green/10 text-brand-green"
                    : order.payment_status === "Payment Rejected"
                    ? "bg-red-100 text-red-600"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {order.payment_status || "Payment Pending"}
              </span>
              {order.utr && <span className="text-brand-muted">UTR: <span className="font-medium text-brand-dark">{order.utr}</span></span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
